import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import type { Session } from '@supabase/supabase-js'

import { lireMonProfil, type Profil } from '../lib/profils'
import { supabase } from '../lib/supabase'

/**
 * Session applicative : la session Supabase ET le profil qui l'accompagne.
 *
 * Pourquoi lire le profil a chaque chargement : `app.est_actif()` interroge
 * `profils` a chaque requete et ne lit pas une pretention du jeton. C'est ce qui
 * rend une revocation instantanee alors que le jeton reste valide jusqu'a son
 * expiration — et c'est voulu, on n'y touche pas.
 *
 * Consequence a l'ecran : sans cette lecture, un compte revoque verrait « aucun
 * dossier », ce qui ressemble a une base vide. On preFere le lui dire.
 */

export type MotifDeconnexion = 'acces-desactive'

export type EtatSession =
  | { readonly statut: 'chargement' }
  | { readonly statut: 'deconnecte'; readonly motif: MotifDeconnexion | null }
  | { readonly statut: 'connecte'; readonly profil: Profil; readonly session: Session }

const ContexteSession = createContext<EtatSession | null>(null)

export function FournisseurSession({ children }: { children: ReactNode }) {
  const [etat, setEtat] = useState<EtatSession>({ statut: 'chargement' })

  // `signOut` declenche un evenement d'authentification qui repasse par
  // `evaluer(null)`. Sans ce relais, le motif serait efface avant d'avoir ete
  // affiche, et l'utilisateur revoque verrait un formulaire muet.
  const motifEnAttente = useRef<MotifDeconnexion | null>(null)

  useEffect(() => {
    let annule = false

    async function evaluer(session: Session | null): Promise<void> {
      if (session === null) {
        const motif = motifEnAttente.current
        motifEnAttente.current = null
        if (!annule) setEtat({ statut: 'deconnecte', motif })
        return
      }

      let profil: Profil | null = null
      try {
        profil = await lireMonProfil(session.user.id)
      } catch {
        // Une lecture de profil qui echoue ne doit pas laisser l'utilisateur
        // dans un etat indetermine : on le traite comme non autorise.
        profil = null
      }
      if (annule) return

      // Profil absent ou inactif : les deux cas se confondent volontairement,
      // la policy empechant un compte revoque de lire sa propre ligne.
      if (profil === null || !profil.actif) {
        motifEnAttente.current = 'acces-desactive'
        await supabase.auth.signOut()
        return
      }

      setEtat({ statut: 'connecte', profil, session })
    }

    void supabase.auth.getSession().then(({ data }) => evaluer(data.session))

    const { data } = supabase.auth.onAuthStateChange((_evenement, session) => {
      void evaluer(session)
    })

    return () => {
      annule = true
      data.subscription.unsubscribe()
    }
  }, [])

  return <ContexteSession.Provider value={etat}>{children}</ContexteSession.Provider>
}

export function useSession(): EtatSession {
  const etat = useContext(ContexteSession)
  if (etat === null) {
    throw new Error('useSession doit etre utilise a l interieur de FournisseurSession.')
  }
  return etat
}
